// Mobile detection and utility functions

/**
 * Check if the device is mobile based on screen width
 * @returns {boolean} True if mobile device
 */
export const isMobile = () => {
  return window.innerWidth <= 768;
};

/**
 * Check if the device is tablet based on screen width
 * @returns {boolean} True if tablet device
 */
export const isTablet = () => {
  return window.innerWidth > 768 && window.innerWidth <= 1024;
};

/**
 * Check if the device is desktop based on screen width
 * @returns {boolean} True if desktop device
 */
export const isDesktop = () => {
  return window.innerWidth > 1024;
};

/**
 * Check if the device supports touch
 * @returns {boolean} True if touch device
 */
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if user prefers reduced motion
 */
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Get the current device type
 * @returns {string} 'mobile', 'tablet', or 'desktop'
 */
export const getDeviceType = () => {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
};

/**
 * Check if the device is in landscape orientation
 * @returns {boolean} True if landscape
 */
export const isLandscape = () => {
  return window.innerWidth > window.innerHeight;
};

/**
 * Get the current viewport dimensions
 * @returns {object} Object with width and height
 */
export const getViewportDimensions = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
};

/**
 * Check if the device is iOS
 * @returns {boolean} True if iOS device
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Check if the device is Android
 * @returns {boolean} True if Android device
 */
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

/**
 * Check if the device is a mobile browser
 * @returns {boolean} True if mobile browser
 */
export const isMobileBrowser = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Get the device pixel ratio
 * @returns {number} Device pixel ratio
 */
export const getDevicePixelRatio = () => {
  return window.devicePixelRatio || 1;
};

/**
 * Check if the device has high DPI
 * @returns {boolean} True if high DPI device
 */
export const isHighDPI = () => {
  return getDevicePixelRatio() > 1;
};

/**
 * Debounce function for resize events
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function for scroll events
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Add mobile-specific classes to body
 */
export const addMobileClasses = () => {
  const body = document.body;
  
  if (isMobile()) {
    body.classList.add('mobile-device');
  }
  
  if (isTouchDevice()) {
    body.classList.add('touch-device');
  }
  
  if (prefersReducedMotion()) {
    body.classList.add('reduced-motion');
  }
  
  if (isHighDPI()) {
    body.classList.add('high-dpi');
  }
};

/**
 * Remove mobile-specific classes from body
 */
export const removeMobileClasses = () => {
  const body = document.body;
  body.classList.remove('mobile-device', 'touch-device', 'reduced-motion', 'high-dpi');
};

/**
 * Initialize mobile utilities
 */
export const initMobileUtils = () => {
  addMobileClasses();
  
  // Listen for resize events
  const handleResize = debounce(() => {
    removeMobileClasses();
    addMobileClasses();
  }, 250);
  
  window.addEventListener('resize', handleResize);
  
  // Listen for orientation changes
  window.addEventListener('orientationchange', () => {
    setTimeout(handleResize, 100);
  });
  
  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
  };
};
